import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Title } from 'react-admin';
import { Box, IconButton, TextField, Button, Tabs, Tab } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MonthlyRecap } from './MonthlyRecap';
import { CasesRecap } from './cases/CasesRecap';
import { OrganicRecap } from './organic/OrganicRecap';
import { AdsPoolRecap } from './ads-pool/AdsPoolRecap';
import { AnalyticsQueryProvider, analyticsQueryClient, RefreshButton } from './recapQuery';

type TabKey = 'growth' | 'cases' | 'organic' | 'ads_pool';

export const MonthlyRecapPage = () => {
    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        () => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    );
    const [tab, setTab] = useState<TabKey>('growth');
    const navigate = useNavigate();

    const isCurrentMonth = selectedMonth === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    const handlePrevMonth = () => {
        const [y, m] = selectedMonth.split('-').map(Number);
        const d = new Date(y, m - 2, 1);
        setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };

    const handleNextMonth = () => {
        const [y, m] = selectedMonth.split('-').map(Number);
        const d = new Date(y, m, 1);
        setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };

    // The tabs share their own QueryClient (recapQuery.tsx): cached 10 min, no refetch on focus.
    return (
        <AnalyticsQueryProvider>
        <Box sx={{ p: 3 }}>
            <Title title="Analytics" />

            <Tabs value={tab} onChange={(_, v) => setTab(v as TabKey)} sx={{ mb: 2 }}>
                <Tab value="growth" label="Growth" />
                <Tab value="cases" label="Cases" />
                <Tab value="organic" label="Organic" />
                <Tab value="ads_pool" label="Ads pool" />
            </Tabs>

            {/* Only the active tab is rendered, so switching to Cases does not
                mount MonthlyRecap's ~30 month-keyed queries (and vice versa).
                The month picker belongs to Growth: Cases cohorts are 8-43 cases
                a month, too thin for monthly percentiles, so it uses its own
                rolling-window control instead. */}
            {tab === 'growth' ? (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <IconButton onClick={handlePrevMonth} size="small"><ChevronLeftIcon /></IconButton>
                        <TextField
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => e.target.value && setSelectedMonth(e.target.value)}
                            size="small"
                            sx={{ width: 200 }}
                            inputProps={{ max: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` }}
                        />
                        <IconButton onClick={handleNextMonth} size="small" disabled={isCurrentMonth}><ChevronRightIcon /></IconButton>
                        {!isCurrentMonth && (
                            <Button size="small" onClick={() => setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)}>
                                Today
                            </Button>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <RefreshButton onRefresh={() => { void analyticsQueryClient.invalidateQueries({ active: true }); }} />
                    </Box>
                    <MonthlyRecap
                        selectedMonth={selectedMonth}
                        onBack={() => navigate('/leads')}
                    />
                </>
            ) : tab === 'cases' ? (
                <CasesRecap />
            ) : tab === 'organic' ? (
                <OrganicRecap />
            ) : (
                <AdsPoolRecap />
            )}
        </Box>
        </AnalyticsQueryProvider>
    );
};
